import { AppSettings } from "@/AppSettings";
import { Trial } from "@/api/dtos";
import { ConfigurationType } from "@/hooks/form/configuration";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Alert, AlertTitle, Box, Button, Chip, Collapse, IconButton, styled } from "@mui/material";
import { FC, useMemo } from "react";
import { useToggle } from "react-use";

const AlertBox = styled(Box)(({ theme }) => ({ 
    marginBottom: theme.spacing(2)
}));

const ActionBox = styled(Box)(({ theme }) => ({ 
    display: 'flex',
    gap: theme.spacing(2)
}));

const TitleAlert = styled(Alert)(({ theme }) => ({ 
    alignItems: 'center'
}));

const TitleAlertTitle = styled(AlertTitle)(({ theme }) => ({ 
    margin: 0,
    padding: 0
}));

interface IConfigurationSourceDataFormLinkAlertProps {
    entityName: string;
    trial?: Trial;
    type: ConfigurationType;
    linked: boolean;
    onLink: () => void;
    onUnlink: () => void;
}

const ConfigurationSourceDataFormLinkAlert: FC<IConfigurationSourceDataFormLinkAlertProps> = ({
    entityName,
    trial,
    type,
    linked,
    onLink,
    onUnlink
}) => {

    const typeText = useMemo(() => {
        switch (type) {
            case ConfigurationType.Event:
                return "Event Configuration";
            case ConfigurationType.Form:
                return "Form Configuration";
            case ConfigurationType.TrialEvent:
                return "Trial Event Configuration";
            case ConfigurationType.Trial:
            default:
                return "Trial Form Configuration";
        }
    }, [type]);

    const [ linkedInfoOpen, toggleLinkedInfo ] = useToggle(false);

    return (
        <AlertBox>
            <TitleAlert
                severity="info"
                variant="filled"
                action={
                    <ActionBox>
                        {
                            linked ?
                                <Button 
                                    color="inherit"
                                    startIcon={
                                        <FontAwesomeIcon icon={AppSettings.icons.unlink} />
                                    }
                                    onClick={onUnlink}
                                >
                                    Unlink
                                </Button> :
                                <Button 
                                    color="inherit"
                                    startIcon={
                                        <FontAwesomeIcon icon={AppSettings.icons.link} />
                                    }
                                    onClick={onLink}
                                >
                                    Link
                                </Button>
                        }
                        <IconButton 
                            color="inherit"
                            onClick={() => {
                                toggleLinkedInfo();
                            }}
                        >
                            <FontAwesomeIcon icon={AppSettings.icons.help} />
                        </IconButton>
                    </ActionBox>
                }
            >
                <TitleAlertTitle>
                    {
                        linked && (
                            <>Linked - {typeText}</>
                        )
                    }
                    {
                        !linked && (
                            <>Unlinked</>
                        )
                    }
                </TitleAlertTitle>
            </TitleAlert>
            <Collapse
                in={linkedInfoOpen}
            >
                <Alert
                    severity="info"
                >
                    <AlertTitle>
                        Configuration Linking
                    </AlertTitle>
                    <p>Configuration linking allows for {entityName}s to share common configuration without the need to redeclare it on every configuration created.</p>
                    {
                        linked && (
                            <>
                                <p>Currently this {entityName} has been linked to the {typeText.toLowerCase()} and will therefore be unavailable for edit.</p>
                                <p>Click the <strong>UNLINK</strong> button below to unlink this configuration.</p>
                                <Button 
                                    color="info"
                                    variant="contained"
                                    startIcon={
                                        <FontAwesomeIcon icon={AppSettings.icons.unlink} />
                                    }
                                    onClick={onUnlink}
                                >
                                    Unlink
                                </Button>
                            </>
                        )
                    }
                    {
                        !linked && (
                            <>
                                <p>Currently this event is not linked to the {typeText.toLowerCase()} and will therefore be available for edit.</p>
                                <p>Click the <strong>LINK</strong> button below to link this configuration.</p>
                                <Button 
                                    color="info"
                                    variant="contained"
                                    startIcon={
                                        <FontAwesomeIcon icon={AppSettings.icons.link} />
                                    }
                                    onClick={onLink}
                                >
                                    Link
                                </Button>
                            </>
                        )
                    }
                </Alert>
            </Collapse>
        </AlertBox>
    )
}

export default ConfigurationSourceDataFormLinkAlert;